import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { CATEGORY_TEXT, apiError, outcomeTone, sourceText } from '../../lib/moderationView';
import { hubService, type ModerationCheck, type ModerationItem, type ModerationMetrics } from '../../services/hub.service';

const TONE = {
  gray: 'bg-gray-100 text-gray-700',
  amber: 'bg-amber-100 text-amber-800',
  green: 'bg-green-100 text-green-800',
  red: 'bg-red-100 text-red-700',
};
const PAGE = 50;

/** What moderation decided for this bot's comments: totals, the list, and the operator's correct / wrong / unhide. */
export function ModerationActivity({ system, botId }: { system: string; botId: string }) {
  const { getToken } = useAuth();
  const [metrics, setMetrics] = useState<ModerationMetrics | null>(null);
  const [rows, setRows] = useState<ModerationItem[]>([]);
  const [total, setTotal] = useState(0);
  const [label, setLabel] = useState('');
  const [offset, setOffset] = useState(0);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [m, list] = await Promise.all([
      hubService.getModerationMetrics(system, botId, 14, getToken()),
      hubService.listModerationItems(system, botId, { limit: PAGE, offset, label: label || undefined }, getToken()),
    ]);
    setMetrics(m);
    setRows(list.rows);
    setTotal(list.total);
  }, [system, botId, label, offset, getToken]);

  useEffect(() => {
    load().catch((e) => setMsg(apiError(e)));
  }, [load]);

  const act = async (fn: () => Promise<unknown>, done: string) => {
    setMsg(null);
    try {
      await fn();
      setMsg(done);
      await load();
    } catch (e) {
      setMsg(apiError(e));
    }
  };

  const t = metrics?.totals;
  return (
    <div className="space-y-4">
      {t && (
        <div className="grid grid-cols-4 gap-3 text-sm">
          <Stat label="Checked (14 days)" value={t.checked} />
          <Stat label="Hidden / deleted" value={t.hidden} />
          <Stat label="Undone by operator" value={t.undone} />
          <Stat label="Undo rate" value={t.undoRate == null ? '—' : `${Math.round(t.undoRate * 100)}%`} />
        </div>
      )}
      <RecentChecks system={system} botId={botId} />
      <h2 className="pt-2 text-sm font-semibold">Flagged comments</h2>
      <div className="flex items-center gap-2 text-sm">
        <span>Show</span>
        <select
          value={label}
          onChange={(e) => {
            setOffset(0);
            setLabel(e.target.value);
          }}
          className="rounded border border-gray-300 px-2 py-1"
        >
          <option value="">All</option>
          <option value="abuse">Abuse</option>
          <option value="spam">Spam</option>
          <option value="complaint">Complaint</option>
        </select>
        <span className="text-gray-500">{total} comments</span>
      </div>
      {msg && <p className="rounded bg-gray-100 p-2 text-sm">{msg}</p>}
      <table className="w-full text-sm">
        <thead className="text-left text-gray-500">
          <tr>
            <th className="py-1">Comment</th>
            <th>Detected</th>
            <th>Result</th>
            <th>Your call</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-gray-100 align-top">
              <td className="py-2 pr-3">
                {r.text}
                <div className="text-xs text-gray-400">{new Date(r.createdAt).toLocaleString()}</div>
              </td>
              <td>
                {CATEGORY_TEXT[r.label ?? 'none']}{' '}
                {r.score != null && <span className="text-xs text-gray-400">{r.score.toFixed(2)}</span>}
              </td>
              <td>
                <span className={`rounded px-2 py-0.5 text-xs ${TONE[outcomeTone(r.outcome)]}`}>{r.outcome ?? '—'}</span>
              </td>
              <td className="space-x-1 whitespace-nowrap">
                {r.verdict ? (
                  <span className="text-xs text-gray-500">{r.verdict === 'correct' ? 'Marked correct' : 'Marked wrong'}</span>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => act(() => hubService.moderationVerdict(system, botId, r.id, 'correct', getToken()), 'Saved: correct')}
                      className="rounded border border-gray-300 px-2 py-0.5 text-xs"
                    >
                      Correct
                    </button>
                    <button
                      type="button"
                      onClick={() => act(() => hubService.moderationVerdict(system, botId, r.id, 'wrong', getToken()), 'Saved: wrong')}
                      className="rounded border border-gray-300 px-2 py-0.5 text-xs"
                    >
                      Wrong
                    </button>
                  </>
                )}
                {r.outcome === 'hidden' && (
                  <button
                    type="button"
                    onClick={() => act(() => hubService.moderationUnhide(system, botId, r.id, getToken()), 'Comment unhidden')}
                    className="rounded border border-red-300 px-2 py-0.5 text-xs text-red-700"
                  >
                    Unhide
                  </button>
                )}
              </td>
            </tr>
          ))}
          {!rows.length && (
            <tr>
              <td colSpan={4} className="py-4 text-center text-gray-500">
                No moderated comments yet. Comments appear here once v1/v2 send them to hub.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div className="flex gap-2 text-sm">
        <button
          type="button"
          disabled={offset === 0}
          onClick={() => setOffset(Math.max(offset - PAGE, 0))}
          className="rounded border border-gray-300 px-2 py-0.5 disabled:opacity-50"
        >
          Previous
        </button>
        <button
          type="button"
          disabled={offset + PAGE >= total}
          onClick={() => setOffset(offset + PAGE)}
          className="rounded border border-gray-300 px-2 py-0.5 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded border border-gray-200 bg-white p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}

/** Every check of this bot, newest first — including normal comments. Text-free: hub never stores normal comments' text. */
function RecentChecks({ system, botId }: { system: string; botId: string }) {
  const { getToken } = useAuth();
  const [rows, setRows] = useState<ModerationCheck[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const load = useCallback(async () => {
    setMsg(null);
    setRows(await hubService.getModerationRecent(system, botId, 50, getToken()));
  }, [system, botId, getToken]);
  useEffect(() => {
    load().catch((e) => setMsg(apiError(e)));
  }, [load]);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">Recent checks</h2>
        <button type="button" onClick={() => load().catch((e) => setMsg(apiError(e)))} className="rounded border border-gray-300 px-2 py-0.5 text-xs">
          Refresh
        </button>
      </div>
      <p className="text-xs text-gray-500">
        Every comment hub checked for this bot (newest first). Normal comments are listed without text — hub does not keep it.
      </p>
      {msg && <p className="rounded bg-gray-100 p-2 text-sm">{msg}</p>}
      <table className="w-full text-xs">
        <thead className="text-left text-gray-500">
          <tr>
            <th className="py-1">Time</th>
            <th>Channel</th>
            <th>Result</th>
            <th>Score</th>
            <th>Decided by</th>
            <th>Mode</th>
            <th>Outcome</th>
            <th>Speed</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-gray-100">
              <td className="py-1">{new Date(r.createdAt).toLocaleString()}</td>
              <td>{r.channel}</td>
              <td>{CATEGORY_TEXT[r.label] ?? r.label}</td>
              <td>{r.score ? r.score.toFixed(2) : '—'}</td>
              <td>{sourceText(r.source, r.ruleId)}</td>
              <td>{r.mode}</td>
              <td>
                <span className={`rounded px-1.5 py-0.5 ${TONE[outcomeTone(r.outcome)]}`}>{r.outcome}</span>
              </td>
              <td>{r.latencyMs} ms</td>
            </tr>
          ))}
          {!rows.length && (
            <tr>
              <td colSpan={8} className="py-3 text-center text-gray-500">
                No checks yet for this bot.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
